import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Linkedin } from "lucide-react";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const values = [
  { title: "Clarity", desc: "Every role deserves a clear definition of excellence \u2014 not a generic job title" },
  { title: "Intelligence", desc: "AI-powered insights grounded in real HR expertise \u2014 not just algorithms" },
  { title: "Growth", desc: "We grow when the professionals we serve grow \u2014 their success is our mission" },
  { title: "Inclusion", desc: "Built for every professional \u2014 every industry, every background, every career stage" },
];

export default function AboutPage() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-navy pt-28 pb-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-white text-3xl sm:text-4xl lg:text-5xl font-bold mb-5">
            Built by HR. Powered by AI. Made for Every Professional.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-dm text-white/70 text-lg max-w-xl mx-auto">
            RoleIQ was born from a simple frustration — that great competency frameworks take weeks to build manually, when they should take seconds.
          </motion.p>
        </div>
      </section>

      {/* Mission Quote */}
      <section className="bg-cream py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <span className="font-playfair text-gold text-6xl">&ldquo;</span>
            <p className="font-playfair text-navy text-xl sm:text-2xl italic leading-relaxed -mt-6">
              We believe every professional deserves clarity about what great looks like in their role — and the community to help them get there.
            </p>
            <span className="font-playfair text-gold text-6xl">&rdquo;</span>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="font-playfair text-navy text-3xl font-semibold mb-6">The Story Behind RoleIQ</h2>
            <div className="space-y-4 font-dm text-navy/70 text-sm leading-relaxed">
              <p>After 14+ years leading Learning, Talent, and Organizational Development at some of India's most respected companies — from Fortune 500 firms to fast-growing startups — the frustration was always the same. Building a competency framework meant weeks of workshops, consultant fees, and generic templates that never quite fit the company's culture.</p>
              <p>RoleIQ was built to change that. By combining deep HR expertise with the power of AI, we can now generate a complete, culture-aligned competency framework for any role in any industry — in 30 seconds. Not a template. A real framework built around your values.</p>
              <p>But RoleIQ is more than a tool. It is a platform for every working professional — to find clarity in their role, grow their career, and connect with a community that understands their journey.</p>
            </div>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: 0.2 }}>
            <img src="https://placehold.co/800x500/0F1E35/C9A84C?text=RoleIQ+Story" alt="RoleIQ" className="rounded-xl border-4 border-gold/20 shadow-lg w-full" />
          </motion.div>
        </div>
      </section>

      {/* Founder */}
      <section className="bg-cream py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="font-playfair text-navy text-3xl font-semibold mb-12">
            Meet the Founder
          </motion.h2>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-white rounded-xl shadow-lg p-10">
            <div className="w-28 h-28 bg-navy/10 rounded-full border-4 border-gold mx-auto mb-5" />
            <h3 className="font-playfair text-navy text-2xl font-bold">PCC Tejinder Singh Sehdev</h3>
            <p className="font-dm text-navy/60 text-sm mb-4">Founder, RoleIQ | Head of Learning, Talent & OD</p>
            <div className="h-px bg-gold/30 max-w-xs mx-auto mb-4" />
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {["ICF PCC Coach", "NLP Practitioner", "Six Sigma Black Belt", "14+ Years HR Leadership"].map((b) => (
                <span key={b} className="bg-gold text-navy font-dm text-xs font-semibold px-3 py-1 rounded-full">{b}</span>
              ))}
            </div>
            <p className="font-dm text-navy/70 text-sm leading-relaxed max-w-lg mx-auto mb-6">
              Senior HR and Organizational Development leader with 14+ years of experience designing enterprise talent architectures, leadership pipelines, and competency frameworks across India and global markets. Certified ICF PCC & ACC Coach with deep expertise in HBDI, Hogan, and True Colors psychometrics.
            </p>
            <a href="#" className="inline-flex items-center gap-2 bg-navy text-white font-dm text-sm px-6 py-2.5 rounded-lg hover:bg-navy/90 transition-colors">
              <Linkedin className="w-4 h-4" /> LinkedIn
            </a>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-4">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="font-playfair text-navy text-3xl text-center font-semibold mb-12">
            What we stand for
          </motion.h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {values.map((v, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white border-t-4 border-gold rounded-xl shadow-md p-6"
              >
                <h3 className="font-playfair text-navy text-xl font-semibold mb-2">{v.title}</h3>
                <p className="font-dm text-navy/60 text-sm">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="bg-navy py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="font-playfair text-gold text-2xl sm:text-3xl italic leading-relaxed"
          >
            Building India's most trusted competency intelligence and professional growth platform
          </motion.p>
        </div>
      </section>
    </div>
  );
}
